import { BILLING_SERVICE_API_BASE } from '../config';
import { getCookie, getCurrentUrl, getPortalUrl, getPortalFqdn } from '../utils';
import * as Sentry from "@sentry/browser";

export abstract class ApiService {

  private ephemeralToken = null as string;
  private usesEphemeralTokens = false as boolean;

  constructor(
    // TODO: Remove the Billing Service API base when all of the other services are broken out.
    private apiBase = BILLING_SERVICE_API_BASE,
    private includeCrossSiteToken = false
  ) { }

  public static buildRootUrl(resource: string, forcePortalDomain: boolean = false) {
    return (forcePortalDomain ? getPortalUrl() : getCurrentUrl()).split(resource)[0] + resource;
  }

  protected setUsesEphemeralToken(useIt: boolean) {
    this.usesEphemeralTokens = useIt;
  }

  protected setEphemeralToken(token: string) {
    this.ephemeralToken = token;
  }

  protected async generateEphemeralToken() {
    // Basically copies the fetchRequest method, but needs to be called on its own to avoid recursive stack overflow
    let headers = {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'X-XSRF-TOKEN': this.includeCrossSiteToken ? decodeURIComponent(getCookie('XSRF-TOKEN')) : undefined,
    } as any;

    try {
      let resp = await fetch('/auth/ephemeralToken', {
        method: 'GET',
        credentials: 'include',
        headers: headers,
        body: null
      });
      let json = await resp.json();

      if (!json.success || !json.token) {
        throw ("Ephemeral token did not return success or has no token: " + JSON.stringify(json));
      }

      this.ephemeralToken = json.token;
    } catch (ex) {
      Sentry.captureException(ex);

      this.ephemeralToken = null;
    }
  }

  protected fetchGet(url: string) {
    return this.fetchRequest(url, 'GET');
  }

  protected fetchPost<T>(url: string, model: T) {
    return this.fetchRequest(url, 'POST', JSON.stringify(model));

  }

  protected fetchPut<T>(url: string, model: T) {
    return this.fetchRequest(url, 'PUT', JSON.stringify(model));
  }

  protected fetchDelete(url: string) {
    return this.fetchRequest(url, 'DELETE');
  }

  protected getParams(params: {}) {
    return Object.entries(params).filter(([k, v]) => v).map(([k, v]) => `${k}=${v}`).join('&');
  }

  protected async fetchRequest(url: string, method: string, body: any = undefined, contentType: string = 'application/json', ignoreBaseUrl: boolean = false, requestOptions: any = {}) {
    let headers = {
      'Accept': 'application/json',
      'X-XSRF-TOKEN': this.includeCrossSiteToken ? decodeURIComponent(getCookie('XSRF-TOKEN')) : undefined,
    } as any;

    // If this object is set to use Ephemeral tokens, generate one on-the-fly.
    if (this.usesEphemeralTokens) {
      await this.generateEphemeralToken();
    }

    if (this.ephemeralToken !== null) {
      headers['X-Galactic-Ephemeral-Token'] = this.ephemeralToken;
    }

    if (contentType) {
      headers['Content-Type'] = contentType;
    }

    return fetch((ignoreBaseUrl ? '' : this.apiBase) + url, {
      method,
      credentials: 'include',
      headers,
      body,
      ...requestOptions,
    }).then(this.toJson);
  }

  private async toJson(response: Response) {
    if (response.status >= 400 && response.status < 600) {
      if (response.status === 403) {
        throw { message: 'You do not have permission to perform that action.', status: response.status };
      } else {
        throw await response.json();
      }
    }

    return await (/^application\/json/.test(response.headers.get('content-type'))
      ? response.json()
      : {});
  }
}
